import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { formatApiErrorDetail } from '../lib/utils';
import {
  getStoredToken, storeTokens, clearTokens, installSessionKeeper, SESSION_EXPIRED_EVENT,
} from '../lib/session';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Renews the login in the background and retries a request that came back
// "not logged in" (see lib/session.js).
installSessionKeeper();

const SERVER_RETRY_MS = 5000;

// What to tell someone whose login attempt got no usable answer.
export const loginErrorMessage = (error) => {
  const status = error?.response?.status;
  const raw = error?.response?.data?.detail;
  if (raw) return formatApiErrorDetail(raw);
  if (!error?.response || status >= 500) {
    return 'The server is not responding right now (it may be restarting). Please try again in a minute.';
  }
  return formatApiErrorDetail(raw) || error.message;
};

// Configure axios to always send token if available
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  // True while the server cannot be reached at start-up (a redeploy or an
  // outage). The login is kept and the check is retried.
  const [serverDown, setServerDown] = useState(false);
  const retryTimer = useRef(null);

  const checkAuth = useCallback(async () => {
    clearTimeout(retryTimer.current);
    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    
    try {
      const { data } = await axios.get(`${API}/auth/me`, { 
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data);
      setServerDown(false);
      setLoading(false);
    } catch (error) {
      if (error?.response?.status === 401) {
        // The server says this login is no longer valid (and renewing it,
        // see lib/session.js, did not help): back to the login page.
        clearTokens();
        setUser(false);
        setServerDown(false);
        setLoading(false);
      } else {
        // No answer, or a server error: keep the login and try again shortly
        // instead of logging the person out.
        setServerDown(true);
        retryTimer.current = setTimeout(() => { checkAuth(); }, SERVER_RETRY_MS);
      }
    }
  }, []);

  useEffect(() => () => clearTimeout(retryTimer.current), []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // The login could not be renewed (a week away, password changed, account
  // removed): go to the login page now rather than on the next failed click.
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      // Store tokens from response body (for custom domain support)
      if (data.access_token) {
        storeTokens(data.access_token, data.refresh_token);
      }
      
      // Set user (without tokens in state)
      setUser({
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role
      });
      // Enrich user state with matched_sales_rep info from /auth/me
      try {
        const me = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${data.access_token}` },
        });
        if (me?.data) setUser(me.data);
      } catch (_) { /* non-fatal */ }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: loginErrorMessage(error),
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      
      // Store tokens from response body (for custom domain support)
      if (data.access_token) {
        storeTokens(data.access_token, data.refresh_token);
      }
      
      // Set user (without tokens in state)
      setUser({
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role
      });
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    clearTokens();
    setUser(false);
  };

  // Super admin: switch the active property. The backend reissues a token whose
  // property_id claim points at the selected property; a full reload re-scopes
  // every page's data + branding to that property.
  const switchProperty = async (propertyId) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/switch-property`,
        { property_id: propertyId },
        { withCredentials: true }
      );
      if (data.access_token) {
        storeTokens(data.access_token, data.refresh_token);
      }
      window.location.href = '/dashboard';
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message,
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, serverDown, login, register, logout, checkAuth, switchProperty }}>
      {children}
    </AuthContext.Provider>
  );
};
